"use client"

import { useState, FormEvent, useEffect } from "react"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle, CheckCircle } from "lucide-react"
import { useSearchParams } from "react-router-dom"

/**
 * Parse WordPress error messages which often contain HTML markup
 * and extract a clean, user-friendly error message.
 * 
 * WordPress returns errors with HTML like:
 * - "<strong>Error:</strong> The password you entered for the username <strong>email</strong> is incorrect. <a href='...'>Lost your password?</a>"
 * - "<strong>Error:</strong> The username <strong>X</strong> is not registered on this site."
 * - "<strong>Error:</strong> The username field is empty."
 * - "<strong>Error:</strong> Invalid username, email address or incorrect password."
 */
function parseWordPressError(rawError: string): string {
  if (!rawError) return 'An error occurred. Please try again.'
  
  // Create a temporary element to safely parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = rawError
  
  // Get text content (strips all HTML tags safely)
  let cleanText = tempDiv.textContent || tempDiv.innerText || rawError
  
  // Clean up common WordPress error patterns
  cleanText = cleanText
    .replace(/^Error:\s*/i, '') // Remove "Error:" prefix
    .replace(/Lost your password\?$/i, '') // Remove the trailing reset link text
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
  
  // Extract email/username if present in the message for better error messages
  const emailMatch = rawError.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/i)
  const usernameMatch = rawError.match(/<strong>([^<]+)<\/strong>/g)
  const extractedIdentifier = emailMatch?.[1] || 
    (usernameMatch && usernameMatch.length > 1 
      ? usernameMatch[1]?.replace(/<\/?strong>/g, '') 
      : null)
  
  // Handle specific WordPress error patterns with friendlier messages
  const lowerText = cleanText.toLowerCase()
  
  // Incorrect password error
  if (lowerText.includes('password you entered') && lowerText.includes('incorrect')) {
    if (extractedIdentifier) {
      return `Incorrect password for ${extractedIdentifier}. Please try again.`
    }
    return 'The password you entered is incorrect. Please try again.'
  }
  
  // Invalid username error (not registered)
  if (lowerText.includes('not registered on this site') || lowerText.includes('invalid_username')) {
    return 'No account found with that username. Try using your email address instead.'
  }
  
  // Unknown username/email
  if (lowerText.includes('unknown username') || lowerText.includes('unknown email')) {
    return 'No account found with that username or email address.'
  }
  
  // Invalid email error
  if (lowerText.includes('invalid email') || lowerText.includes('invalid_email')) {
    return 'No account found with that email address.'
  }
  
  // Generic authentication failed
  if (lowerText.includes('invalid username, email address or incorrect password') || 
      lowerText.includes('authentication_failed')) {
    return 'Invalid username, email, or password. Please check your credentials.'
  }
  
  // Empty username
  if (lowerText.includes('username field is empty') || lowerText.includes('empty_username')) {
    return 'Please enter your username or email address.'
  }
  
  // Empty password
  if (lowerText.includes('password field is empty') || lowerText.includes('empty_password')) {
    return 'Please enter your password.'
  }
  
  // Password reset errors
  if (lowerText.includes('expired') && (lowerText.includes('key') || lowerText.includes('link'))) {
    return 'This password reset link has expired. Please request a new one.'
  }
  
  if (lowerText.includes('invalid') && (lowerText.includes('key') || lowerText.includes('link') || lowerText.includes('reset'))) {
    return 'Invalid password reset link. Please request a new one.'
  }
  
  // Account locked/too many attempts (common security plugin messages)
  if (lowerText.includes('too many') && (lowerText.includes('attempt') || lowerText.includes('login'))) {
    return 'Too many failed login attempts. Please try again later.'
  }
  
  if (lowerText.includes('locked') || lowerText.includes('blocked')) {
    return 'Your account has been temporarily locked. Please try again later or contact support.'
  }
  
  // Email/username required (from our PHP handler)
  if (lowerText.includes('email or username is required')) {
    return 'Please enter your email or username.'
  }
  
  // If we cleaned the text and it's meaningful, return it
  if (cleanText && cleanText.length > 0 && cleanText.length < 200) {
    return cleanText
  }
  
  // Fallback message
  return 'Login failed. Please check your credentials and try again.'
}

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [resetEmail, setResetEmail] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showResetForm, setShowResetForm] = useState(false)
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const [resetKey, setResetKey] = useState("")
  const [resetLogin, setResetLogin] = useState("")
  
  const { login } = useWordPressAuth()

  // Check URL parameters for password reset flow
  useEffect(() => {
    const action = searchParams.get('action')
    const key = searchParams.get('key')
    const loginParam = searchParams.get('login')
    
    if ((action === 'rp' || action === 'resetpass') && key && loginParam) {
      // User clicked password reset link from email
      setResetKey(key)
      setResetLogin(loginParam)
      setShowNewPasswordForm(true)
      setShowResetForm(false)
      
      // Verify the key is valid
      verifyResetKey(key, loginParam)
    }
  }, [searchParams])

  const verifyResetKey = async (key: string, login: string) => {
    try {
      const wpConfig = typeof window !== 'undefined' && (window as any).WP_COCKPIT
      const adminAjaxUrl = wpConfig?.adminAjaxUrl || '/wp-admin/admin-ajax.php'

      const response = await fetch(adminAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'tt_verify_reset_key',
          key: key,
          login: login
        }),
        credentials: 'same-origin'
      })

      const data = await response.json()
      
      if (!data.success) {
        const errorMsg = data.data?.message || 'invalid_key'
        if (errorMsg === 'expired_key') {
          setError('This password reset link has expired. Please request a new one.')
        } else {
          setError('Invalid password reset link. Please request a new one.')
        }
        setShowNewPasswordForm(false)
      }
    } catch (err) {
      setError('An error occurred verifying your reset link.')
      setShowNewPasswordForm(false)
    }
  }

  // Get logo URL and white label settings from WordPress config
  const wpConfig = typeof window !== 'undefined' ? (window as any).WP_COCKPIT : null
  const whiteLabel = wpConfig?.whiteLabel || {}
  const logoUrl = whiteLabel.logo || (wpConfig?.assetsUrl 
    ? wpConfig.assetsUrl.replace('/assets/', '/') + 'logo.png'
    : '/logo.png')
  const loginTitle = whiteLabel.loginTitle || 'Cockpit'
  const loginSubtitle = whiteLabel.loginSubtitle || 'Sign in to your account'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError("")
    setIsLoading(true)

    try {
      const wpConfig = typeof window !== 'undefined' && (window as any).WP_COCKPIT
      const adminAjaxUrl = wpConfig?.adminAjaxUrl || '/wp-admin/admin-ajax.php'

      const response = await fetch(adminAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'tt_login',
          username,
          password
        }),
        credentials: 'same-origin'
      })

      const data = await response.json()

      if (data.success) {
        const isDev = (window as any).WP_COCKPIT?.devMode || false
        if (isDev) {
          console.log('[LoginPage] Login successful, response data:', data.data)
          console.log('[LoginPage] Before update, WP_COCKPIT:', (window as any).WP_COCKPIT)
        }
        
        // Update WP_COCKPIT global with fresh user data and new nonce
        if (typeof window !== 'undefined' && (window as any).WP_COCKPIT) {
          (window as any).WP_COCKPIT = {
            ...(window as any).WP_COCKPIT,
            isLoggedIn: 'true',
            userId: String(data.data.userId),
            username: data.data.username,
            userEmail: data.data.userEmail,
            displayName: data.data.displayName,
            roles: data.data.roles,
            restNonce: data.data.restNonce // Fresh nonce from login response
          }
          
          if (isDev) {
            console.log('[LoginPage] After update, WP_COCKPIT:', (window as any).WP_COCKPIT)
          }
        }

        // Keep loading state active while redirecting
        // Login successful - call the auth context login (which will reload the page)
        login({
          id: data.data.userId,
          username: data.data.username,
          email: data.data.userEmail,
          displayName: data.data.displayName,
          roles: data.data.roles
        })
      } else {
        setError(parseWordPressError(data.data?.message || 'Login failed. Please check your credentials.'))
        setIsLoading(false)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault()
    setError("")
    setResetSuccess(false)
    setIsLoading(true)

    try {
      const wpConfig = typeof window !== 'undefined' && (window as any).WP_COCKPIT
      const adminAjaxUrl = wpConfig?.adminAjaxUrl || '/wp-admin/admin-ajax.php'

      const response = await fetch(adminAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'tt_lost_password',
          user_login: resetEmail
        }),
        credentials: 'same-origin'
      })

      const data = await response.json()
      
      if (data.success) {
        setResetSuccess(true)
        setResetEmail("")
        setIsLoading(false)
        
        // Auto switch back to login after 5 seconds
        setTimeout(() => {
          setShowResetForm(false)
          setResetSuccess(false)
        }, 5000)
      } else {
        setError(parseWordPressError(data.data?.message || 'Failed to send reset link. Please try again.'))
        setIsLoading(false)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  const handleNewPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError("")
    setPasswordResetSuccess(false)

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.')
      return
    }

    setIsLoading(true)

    try {
      const wpConfig = typeof window !== 'undefined' && (window as any).WP_COCKPIT
      const adminAjaxUrl = wpConfig?.adminAjaxUrl || '/wp-admin/admin-ajax.php'

      const response = await fetch(adminAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'tt_reset_password',
          key: resetKey,
          login: resetLogin,
          password: newPassword
        }),
        credentials: 'same-origin'
      })

      const data = await response.json()
      
      if (data.success) {
        setPasswordResetSuccess(true)
        setNewPassword("")
        setConfirmPassword("")
        setIsLoading(false)
        
        // Auto redirect to login after 3 seconds
        setTimeout(() => {
          setShowNewPasswordForm(false)
          setPasswordResetSuccess(false)
          setResetKey("")
          setResetLogin("")
          // Remove URL parameters
          window.history.replaceState({}, '', window.location.pathname)
        }, 3000)
      } else {
        setError(parseWordPressError(data.data?.message || 'Failed to reset password. Please try again.'))
        setIsLoading(false)
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      {/* Loading overlay during redirect */}
      {isLoading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-lg font-medium text-foreground">
              {showResetForm ? "Sending reset link..." : showNewPasswordForm ? "Resetting password..." : "Signing you in..."}
            </p>
          </div>
        </div>
      )}
      
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 rounded-full" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">{loginTitle}</CardTitle>
          <CardDescription>
            {showResetForm ? "Reset your password" : showNewPasswordForm ? "Choose a new password" : loginSubtitle}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showNewPasswordForm ? (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {passwordResetSuccess && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your password has been reset successfully! Redirecting to login...
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading || passwordResetSuccess}
                  required
                  minLength={8}
                />
                <p className="text-sm text-muted-foreground">
                  Password must be at least 8 characters long.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading || passwordResetSuccess}
                  required
                  minLength={8}
                />
              </div>

              {!passwordResetSuccess && (
                <Button 
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white" 
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </Button>
              )}
            </form>
          ) : !showResetForm ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="username">Username or Email</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username or email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetForm(true)
                    setError("")
                  }}
                  className="text-sm text-primary hover:underline"
                  disabled={isLoading}
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {resetSuccess && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Password reset link has been sent to your email. Please check your inbox.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="resetEmail">Email Address</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="Enter your email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <Button 
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetForm(false)
                    setResetSuccess(false)
                    setError("")
                  }}
                  className="text-sm text-primary hover:underline"
                  disabled={isLoading}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
